import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useApp, COUNTRIES } from '../../contexts/AppContext';

const CountrySelector = () => {
  const { selectedCountry, switchCountry, countryConfig } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryChange = (countryCode) => {
    switchCountry(countryCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:border-indigo-300 transition-all shadow-sm hover:shadow-md"
      >
        <Globe className="w-4 h-4 text-gray-600" />
        <span className="text-2xl">{countryConfig.flag}</span>
        <span className="font-medium text-gray-900">
          {countryConfig.name}
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full mt-2 right-0 w-64 bg-white rounded-xl shadow-xl border-2 border-gray-200 overflow-hidden z-50 animate-fade-in-down">
          <div className="p-2">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Select Market
            </div>
            
            {Object.values(COUNTRIES).map((country) => {
              const isSelected = selectedCountry === country.code;
              
              return (
                <button
                  key={country.code}
                  onClick={() => handleCountryChange(country.code)}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-all ${
                    isSelected
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{country.flag}</span>
                    <div className="text-left">
                      <div className="font-semibold">{country.name}</div>
                      <div className="text-xs text-gray-500">
                        {country.exchanges.join(', ')}
                      </div>
                    </div>
                  </div>
                  
                  {isSelected && (
                    <Check className="w-5 h-5 text-indigo-600" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Market Info */}
          <div className="border-t-2 border-gray-200 p-3 bg-gray-50">
            <div className="text-xs text-gray-600">
              <div className="flex items-center justify-between mb-1">
                <span>Market Hours:</span>
                <span className="font-medium text-gray-900">
                  {countryConfig.marketHours}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Currency:</span>
                <span className="font-medium text-gray-900">
                  {countryConfig.currency}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
